import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import AllKitsPage from './pages/AllKitsPage';
import BundlesPage from './pages/BundlesPage';
import KitDetailPage from './pages/KitDetailPage';
import BundleDetailPage from './pages/BundleDetailPage';
import CheckoutModal from './components/checkout/CheckoutModal';
import { useAuth } from './context/AuthContext';
import { kits, bundles, stats, faqs, categories } from './data/products';

// Navbar Component
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => {
    if (path === '/kits') {
      return location.pathname === '/kits' || location.pathname.startsWith('/kits/');
    }
    if (path === '/bundles') {
      return location.pathname === '/bundles' || location.pathname.startsWith('/bundles/');
    }
    return location.pathname === path;
  };

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-content">
        <div className="logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <span className="logo-icon">⚡</span>
          <span className="logo-text">Tuple Camp</span>
        </div>
        <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
          <Link to="/kits" className={`nav-link ${isActive('/kits') ? 'active' : ''}`}>Interview Kits</Link>
          <Link to="/bundles" className={`nav-link ${isActive('/bundles') ? 'active' : ''}`}>Bundles</Link>
          <Link to="/contact" className={`nav-link ${isActive('/contact') ? 'active' : ''}`}>Contact</Link>
        </div>
        <button
          className="mobile-menu-btn"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? '✕' : '☰'}
        </button>
      </div>
    </nav>
  );
};

// Hero Component
const Hero = () => {
  return (
    <section className="hero">
      <div className="hero-bg">
        <div className="grid-overlay"></div>
        <div className="glow glow-1"></div>
        <div className="glow glow-2"></div>
      </div>
      <div className="hero-content">
        <div className="badge">⚡ {stats.totalQuestions} Curated Questions • {stats.totalKits} Technologies</div>
        <h1 className="hero-title">
          Crack Your Next
          <span className="gradient-text"> Tech Interview</span>
        </h1>
        <p className="hero-subtitle">
          Get interview-ready in days, not weeks. Curated questions from real interviews at top tech companies.
        </p>
        <p className="hero-description">
          From CSS to System Design — 16 comprehensive interview kits covering every technology you need.
          Real questions, detailed explanations, and patterns that actually get asked.
        </p>
        <div className="hero-ctas">
          <Link to="/kits" className="btn btn-primary btn-large">
            Browse All Kits
          </Link>
          <Link to="/bundles/complete-collection" className="btn btn-outline btn-large">
            Get Complete Collection - ₹999
          </Link>
        </div>
        <div className="trust-badges">
          <div className="trust-item">
            <span className="check">✓</span> Instant PDF Download
          </div>
          <div className="trust-item">
            <span className="check">✓</span> From Real Interviews
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Bar Component
const StatsBar = () => {
  const statItems = [
    { value: stats.totalKits, label: 'Technologies' },
    { value: stats.totalQuestions, label: 'Questions' },
    { value: stats.totalBundles, label: 'Bundles' },
  ];

  return (
    <section className="stats-bar">
      <div className="container">
        <div className="stats-grid">
          {statItems.map((stat, i) => (
            <div key={i} className="stat-item">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Featured Kits Component
const FeaturedKits = () => {
  const { openCheckout } = useAuth();
  const featuredKits = [
    kits.find(k => k.id === 'javascript-kit'),
    kits.find(k => k.id === 'react-kit'),
    kits.find(k => k.id === 'dsa-kit'),
    kits.find(k => k.id === 'system-design-kit'),
  ].filter(Boolean);

  return (
    <section className="featured-kits-section">
      <div className="container">
        <h2 className="section-title center">Popular Interview Kits</h2>
        <p className="section-subtitle">Most purchased kits for tech interviews</p>
        <div className="kits-grid">
          {featuredKits.map(kit => (
            <div key={kit.id} className="kit-card">
              <div className="kit-card-header">
                <span className="kit-icon">{kit.icon}</span>
                <span className="kit-category-badge">{categories[kit.category]?.name}</span>
              </div>
              <h3 className="kit-name">{kit.name}</h3>
              <p className="kit-description">{kit.description}</p>
              <div className="kit-stats">
                <span className="kit-questions">{kit.questionCount} Questions</span>
              </div>
              <div className="kit-topics">
                {kit.topics.slice(0, 4).map((topic, i) => (
                  <span key={i} className="kit-topic-tag">{topic}</span>
                ))}
                {kit.topics.length > 4 && (
                  <span className="kit-topic-more">+{kit.topics.length - 4} more</span>
                )}
              </div>
              <div className="kit-footer">
                <div className="kit-price">
                  <span className="original-price">₹{kit.price * 10}</span>
                  <span className="current-price">
                    <span className="currency">₹</span>
                    <span className="amount">{kit.price}</span>
                  </span>
                </div>
                <div className="kit-actions">
                  <Link to={`/kits/${kit.slug}`} className="btn btn-outline btn-small">
                    View Details
                  </Link>
                  <button
                    onClick={() => openCheckout({ id: kit.id, name: kit.name, price: kit.price })}
                    className="btn btn-primary btn-small"
                  >
                    Get Kit
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="view-all-cta">
          <Link to="/kits" className="btn btn-outline">
            View All 16 Kits →
          </Link>
        </div>
      </div>
    </section>
  );
};

// Categories Section
const CategoriesSection = () => {
  const categoryList = Object.values(categories).map(cat => ({
    ...cat,
    count: kits.filter(k => k.category === cat.id).length,
    kits: kits.filter(k => k.category === cat.id),
  }));

  return (
    <section className="categories-section">
      <div className="container">
        <h2 className="section-title center">Browse by Category</h2>
        <p className="section-subtitle">Find kits for your target role</p>
        <div className="categories-grid">
          {categoryList.map(cat => (
            <Link
              key={cat.id}
              to={`/kits?category=${cat.id}`}
              className="category-card"
            >
              <span className="category-icon">{cat.icon}</span>
              <h3 className="category-name">{cat.name}</h3>
              <p className="category-count">{cat.count} Kits</p>
              <div className="category-kits-preview">
                {cat.kits.slice(0, 3).map(k => (
                  <span key={k.id} className="kit-icon-tiny">{k.icon}</span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Bundle Showcase Component
const BundleShowcase = () => {
  const { openCheckout } = useAuth();
  const completeBundle = bundles.find(b => b.id === 'complete-bundle');
  const featuredBundles = bundles.filter(b => b.id !== 'complete-bundle').slice(0, 3);

  return (
    <section className="bundle-showcase-section">
      <div className="container">
        <h2 className="section-title center">Save More with Bundles</h2>
        <p className="section-subtitle">Get multiple kits at discounted prices</p>

        {/* Complete Bundle Feature */}
        {completeBundle && (
          <div className="complete-bundle-showcase">
            <div className="showcase-badge">Best Value</div>
            <div className="showcase-content">
              <div className="showcase-info">
                <span className="showcase-icon">{completeBundle.icon}</span>
                <h3>{completeBundle.name}</h3>
                <p>{completeBundle.description}</p>
                <div className="showcase-stats">
                  <span>{completeBundle.kitCount} Kits</span>
                  <span>•</span>
                  <span>{completeBundle.questionCount} Questions</span>
                  <span>•</span>
                  <span>Save ₹{completeBundle.savings}+</span>
                </div>
              </div>
              <div className="showcase-pricing">
                <div className="price-info">
                  <span className="original-price">₹{completeBundle.originalPrice}</span>
                  <span className="current-price">₹{completeBundle.price}</span>
                </div>
                <button
                  onClick={() => openCheckout({ id: completeBundle.id, name: completeBundle.name, price: completeBundle.price })}
                  className="btn btn-primary btn-large"
                >
                  Get All Kits
                </button>
                <Link to={`/bundles/${completeBundle.slug}`} className="view-details">
                  View details →
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Other Bundles */}
        <div className="other-bundles-preview">
          {featuredBundles.map(bundle => (
            <div key={bundle.id} className="bundle-preview-card">
              <div className="bundle-preview-header">
                <span className="bundle-icon">{bundle.icon}</span>
                <h3>{bundle.name}</h3>
              </div>
              <p className="bundle-preview-meta">
                {bundle.kitCount} kits • Save ₹{bundle.savings}
              </p>
              <div className="bundle-preview-footer">
                <span className="bundle-price">₹{bundle.price}</span>
                <Link to={`/bundles/${bundle.slug}`} className="btn btn-outline btn-small">
                  View
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="view-all-bundles-cta">
          <Link to="/bundles" className="btn btn-outline">
            View All Bundles →
          </Link>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Component
const WhyChooseUs = () => {
  const reasons = [
    { icon: '✓', text: 'Curated from real interviews at top companies' },
    { icon: '✓', text: 'Code examples included with explanations' },
    { icon: '✓', text: 'Visual diagrams for complex topics' },
    { icon: '✓', text: 'Quick reference cheat sheets' },
    { icon: '✓', text: 'Affordable pricing starting from ₹99' },
  ];

  return (
    <section className="why-choose-section">
      <div className="container">
        <h2 className="section-title center">Why Tuple Camp?</h2>
        <p className="section-subtitle">Interview prep that actually works</p>
        <div className="reasons-grid">
          {reasons.map((reason, i) => (
            <div key={i} className="reason-item">
              <span className="reason-icon">{reason.icon}</span>
              <span className="reason-text">{reason.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Component
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="faq-section" id="faq">
      <div className="container">
        <h2 className="section-title center">
          Frequently Asked Questions
        </h2>
        <div className="faq-list">
          {faqs.map((faq, i) => (
            <div
              className={`faq-item ${openIndex === i ? 'open' : ''}`}
              key={i}
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
            >
              <div className="faq-question">
                <span>{faq.q}</span>
                <span className="faq-toggle">{openIndex === i ? '−' : '+'}</span>
              </div>
              <div className="faq-answer">
                <p>{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA Component
const FinalCTA = () => {
  return (
    <section className="final-cta-section">
      <div className="container">
        <div className="final-cta-content">
          <h2 className="final-cta-title">
            Ready to Ace Your Tech Interview?
          </h2>
          <p className="final-cta-subtitle">
            {stats.totalQuestions} questions across {stats.totalKits} technologies. Get started today.
          </p>
          <div className="final-cta-actions">
            <Link to="/kits" className="btn btn-primary btn-large">
              Browse All Kits
            </Link>
            <Link to="/bundles/complete-collection" className="btn btn-outline btn-large">
              Get Complete Collection - ₹999
            </Link>
          </div>
          <p className="guarantee">
            ✓ Instant download · ✓ Free updates
          </p>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-main">
          <div className="footer-column">
            <h4>Products</h4>
            <ul>
              <li><Link to="/kits">All Interview Kits</Link></li>
              <li><Link to="/bundles">Bundle Packages</Link></li>
              <li><Link to="/bundles/complete-collection">Complete Collection</Link></li>
            </ul>
          </div>
          <div className="footer-column">
            <h4>Categories</h4>
            <ul>
              <li><Link to="/kits?category=frontend">Frontend</Link></li>
              <li><Link to="/kits?category=backend">Backend</Link></li>
              <li><Link to="/kits?category=devops">DevOps & Cloud</Link></li>
              <li><Link to="/kits?category=core">Core CS</Link></li>
            </ul>
          </div>
          <div className="footer-column">
            <h4>Legal</h4>
            <ul>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
            </ul>
          </div>
          <div className="footer-column">
            <h4>Support</h4>
            <ul>
              <li><Link to="/contact">Contact Us</Link></li>
              <li><a href="mailto:support@tuplecamp.com">support@tuplecamp.com</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-brand">
            <span className="logo-icon">⚡</span>
            <span className="logo-text">Tuple Camp</span>
          </div>
          <p className="copyright">
            © {new Date().getFullYear()} Tuple Camp. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const HomePage = () => {
  return (
    <>
      <Hero />
      <StatsBar />
      <FeaturedKits />
      <CategoriesSection />
      <BundleShowcase />
      <WhyChooseUs />
      <FAQ />
      <FinalCTA />
    </>
  );
};

// Main App
function App() {
  return (
    <div className="app">
      <Navbar />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/kits" element={<AllKitsPage />} />
        <Route path="/kits/:slug" element={<KitDetailPage />} />
        <Route path="/bundles" element={<BundlesPage />} />
        <Route path="/bundles/:slug" element={<BundleDetailPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/contact" element={<ContactPage />} />
      </Routes>
      <Footer />
      <CheckoutModal />
    </div>
  );
}

export default App;
